import { useCallback, useMemo, useRef } from 'react'

import { useSelectedBranchNumericId } from '@/pages/User/pages/Home/components/TreePanel/TreePanelContext'
import {
	GetStructuresStructureV2ApiArg,
	StructureMetadataDtoRead,
	StructureType,
} from '@/rtkApi/mddApiSliceGeneratedV2'
import { useLazyGetStructuresQuery } from '@/rtkApi/mddApiV2'

export type StructureSearchOption = {
	code?: string
	fullPath?: string
	id?: number
	lockedFor?: { fullName?: string; id?: number; loginName?: string }
	lockedTime?: string
	name?: string
	parentStructureId?: number
	permissions?: StructureMetadataDtoRead['permissions']
	status?: StructureMetadataDtoRead['status']
	structureId?: number
	structureUuid?: string
	type?: StructureType
	uuid?: string
}

export const STRUCTURE_SEARCH_PAGE_SIZE = 30

export type UseStructureSearchOptions = Pick<
	GetStructuresStructureV2ApiArg,
	'locked' | 'lockedByMe' | 'pageSize' | 'startFromId' | 'status'
> & {
	types?: StructureType[]
}

const mapToOption = (item: StructureMetadataDtoRead): StructureSearchOption => ({
	id: item.id,
	uuid: item.uuid,
	structureId: item.id,
	structureUuid: item.uuid,
	name: item.name,
	code: item.code,
	type: item.type,
	fullPath: item.fullPath,
	lockedFor: item.lockedFor,
	lockedTime: item.lockedTime,
	parentStructureId: item.parent?.id,
	permissions: item.permissions,
	status: item.status,
})

export const useStructureSearch = (options?: UseStructureSearchOptions) => {
	const {
		types,
		pageSize = STRUCTURE_SEARCH_PAGE_SIZE,
		startFromId,
		locked,
		lockedByMe,
		status,
	} = options ?? {}

	const typesRef = useRef<StructureType[] | undefined>(types)
	typesRef.current = types
	const startFromIdRef = useRef(startFromId)
	startFromIdRef.current = startFromId
	const lockedRef = useRef(locked)
	lockedRef.current = locked
	const lockedByMeRef = useRef(lockedByMe)
	lockedByMeRef.current = lockedByMe
	const statusRef = useRef(status)
	statusRef.current = status

	const [triggerSearch, { data: searchData, isFetching }] =
		useLazyGetStructuresQuery()
	const branchId = useSelectedBranchNumericId()

	const loadPage = useCallback(
		async (
			inputValue?: string,
			pageNumber = 1,
		): Promise<StructureSearchOption[]> => {
			if (branchId == null) return []
			const result = await triggerSearch({
				branchId,
				searchTerm: inputValue || undefined,
				type: typesRef.current,
				startFromId: startFromIdRef.current,
				pageSize,
				pageNumber,
				locked: lockedRef.current,
				lockedByMe: lockedByMeRef.current,
				status: statusRef.current,
			})

			return (result.data ?? []).map(mapToOption)
		},
		[branchId, triggerSearch, pageSize],
	)

	const loadOptions = useCallback(
		async (inputValue?: string): Promise<StructureSearchOption[]> => {
			return loadPage(inputValue, 1)
		},
		[loadPage],
	)

	const loadInitialOptions = useCallback(async (): Promise<StructureSearchOption[]> => {
		if (branchId == null) return []
		const result = await triggerSearch({
			branchId,
			type: typesRef.current,
			startFromId: startFromIdRef.current,
			pageSize,
			locked: lockedRef.current,
			lockedByMe: lockedByMeRef.current,
			status: statusRef.current,
		})

		return (result.data ?? []).map(mapToOption)
	}, [branchId, triggerSearch, pageSize])

	const searchOptions = useMemo(
		() => (searchData ?? []).map(mapToOption),
		[searchData],
	)

	return {
		loadOptions,
		loadPage,
		loadInitialOptions,
		searchOptions,
		isFetching,
		triggerSearch,
	}
}

export const formatStructureOptionLabel = (
	option: StructureSearchOption,
	formatOptionLabelMeta?: { context: 'menu' | 'value' },
) => {
	const name = option.name || ''
	const path = option.fullPath
		? option.fullPath.includes('>')
			? option.fullPath.substring(0, option.fullPath.lastIndexOf('>'))
			: option.fullPath
		: ''

	// For selected value, show compact version
	if (formatOptionLabelMeta?.context === 'value') {
		return (
			<span className="truncate flex items-center">
				<span>{name}</span>
				{path && <span className="text-xs opacity-70 ml-1">({path})</span>}
			</span>
		)
	}

	// For dropdown menu options
	return (
		<span className="truncate block">
			<span className="font-medium">{name}</span>
			{path && (
				<span className="text-xs opacity-70 ml-1 block">({path})</span>
			)}
		</span>
	)
}

